import type React from 'react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';

interface AuthGuardProps {
  children: React.ReactNode;
  permissions?: string[];
  requireAny?: boolean; // If true, user needs ANY of the permissions, otherwise ALL
  fallback?: React.ReactNode;
  redirectTo?: string;
}

/**
 * AuthGuard component that conditionally renders children based on authentication
 * and permission requirements.
 */
export function AuthGuard({
  children,
  permissions = [],
  requireAny = false,
  fallback = <div>Access denied</div>,
  redirectTo,
}: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasPermission, hasAnyPermission } = useAuth();
  const [debugCountdown, setDebugCountdown] = useState<number | null>(null);

  console.log('[AuthGuard] Auth state:', { isAuthenticated, isLoading });

  // DEBUG: Add 20 second delay before redirect
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log('[AuthGuard] Not authenticated, starting 20s countdown before redirect');
      setDebugCountdown(20);

      const interval = setInterval(() => {
        setDebugCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          console.log(`[AuthGuard] Redirecting in ${prev - 1} seconds...`);
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
    return undefined;
  }, [isLoading, isAuthenticated]);

  // Show loading state
  if (isLoading) {
    console.log('[AuthGuard] Still loading, showing loading state');
    return <div>Loading...</div>;
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    // DEBUG: Show countdown before redirect
    if (debugCountdown !== null && debugCountdown > 0) {
      return (
        <div style={{ padding: '20px', fontFamily: 'monospace' }}>
          <h2>AuthGuard Debug Mode</h2>
          <p>Not authenticated. Redirecting in {debugCountdown} seconds...</p>
          <pre>{JSON.stringify({ isAuthenticated, isLoading }, null, 2)}</pre>
        </div>
      );
    }

    console.log('[AuthGuard] Not authenticated, redirecting to login');

    if (redirectTo) {
      // For absolute URLs (cross-origin or external), use window.location
      if (redirectTo.startsWith('http')) {
        window.location.href = redirectTo;
        return null;
      }

      // For relative paths, use React Router Navigate (no page reload)
      return <Navigate to={redirectTo} replace />;
    }

    // Default: redirect to Home login page using environment variable
    const shellUrl = import.meta.env.VITE_SHELL_URL || '';
    window.location.href = `${shellUrl}/login`;
    return null;
  }

  // Check permissions if specified
  if (permissions.length > 0) {
    const hasRequiredPermissions = requireAny
      ? hasAnyPermission(permissions)
      : permissions.every((permission) => hasPermission(permission));

    if (!hasRequiredPermissions) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
}

interface PermissionGuardProps {
  children: React.ReactNode;
  permissions: string[];
  requireAny?: boolean;
  fallback?: React.ReactNode;
}

/**
 * PermissionGuard component that only checks permissions (assumes user is authenticated)
 */
export function PermissionGuard({
  children,
  permissions,
  requireAny = false,
  fallback = null,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const hasRequiredPermissions = requireAny
    ? hasAnyPermission(permissions)
    : permissions.every((permission) => hasPermission(permission));

  if (!hasRequiredPermissions) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
